import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProperty, PropertyProvider } from "@/hooks/useProperty";
import BottomNav from "@/components/layout/BottomNav";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Schedule from "@/pages/Schedule";
import Delivery from "@/pages/Delivery";
import Comms from "@/pages/Comms";
import Account from "@/pages/Account";
import Requests from "@/pages/Requests";
import PaymentSuccess from "@/pages/PaymentSuccess";

// Property selector modal
function PropertySelector({ properties, onSelect }) {
  return (
    <div className="min-h-screen bg-navy flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="property-selector">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-12 h-12 rounded-full bg-gold mb-4"></div>
        <h1 className="font-dm text-xl font-semibold text-white mb-2 uppercase tracking-wide">
          Select Property
        </h1>
        <p className="text-white/50 text-xs font-dm mb-8">
          Choose a property to continue
        </p>

        <div className="w-full max-w-sm sm:max-w-lg space-y-3">
          {properties.map((property) => (
            <button
              key={property.id}
              onClick={() => onSelect(property)}
              className="w-full bg-cream rounded-2xl p-4 text-left hover:bg-white transition-colors"
              data-testid={`property-${property.id}`}
            >
              <p className="font-dm text-sm font-bold text-navy">{property.name}</p>
              <p className="font-dm text-xs text-slate mt-1">{property.address}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children, requiredTab }) {
  const { isAuthenticated, loading: authLoading, hasTab } = useAuth();
  const { selectedProperty, needsSelection, properties, selectProperty, loading: propLoading } = useProperty();
  const location = useLocation();

  if (authLoading || propLoading) {
    return (
      <div className="min-h-screen bg-slate-light flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-navy border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsSelection) {
    return <PropertySelector properties={properties} onSelect={selectProperty} />;
  }

  // Check tab permission (account is always allowed)
  if (requiredTab && requiredTab !== 'account' && !hasTab(requiredTab)) {
    return <Navigate to="/overview" replace />;
  }

  return children;
}

// Main app layout with bottom nav
function AppLayout({ user }) {
  const { hasTab } = useAuth();
  const allowedTabs = user?.allowed_tabs || [];

  return (
    <div className="min-h-screen bg-slate-light">
      <Routes>
        <Route 
          path="/overview" 
          element={
            <ProtectedRoute requiredTab="landscape_overview">
              <Overview user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/schedule" 
          element={
            <ProtectedRoute requiredTab="landscape_schedule">
              <Schedule user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/delivery" 
          element={
            <ProtectedRoute requiredTab="landscape_photos">
              <Delivery user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/comms" 
          element={
            <ProtectedRoute requiredTab="communication">
              <Comms user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/account" 
          element={
            <ProtectedRoute requiredTab="account">
              <Account user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/requests" 
          element={
            <ProtectedRoute>
              <Requests user={user} />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
      <BottomNav allowedTabs={allowedTabs} />
    </div>
  );
}

// Root component with auth check
function AppRoot() {
  const { isAuthenticated, user, loading, getDefaultTab } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated && location.pathname === '/login') {
      navigate(getDefaultTab(), { replace: true });
    }
  }, [loading, isAuthenticated, location.pathname, navigate, getDefaultTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/*" element={<AppLayout user={user} />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PropertyProvider>
          <AppRoot />
        </PropertyProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
